import React, { useMemo, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import type { Goal, MetricType, TimeframeType } from '../../types';
import { SearchableSelect } from '../ui/SearchableSelect';
import { getAllDivisionsForSelection, getAllTeamsForSelection, getAllLoanOfficersForSelection } from '../../data/mockData';
interface GoalFormProps {
  goal?: Goal;
  onSave: (goal: Goal) => void;
  onCancel: () => void;
}
export function GoalForm({
  goal,
  onSave,
  onCancel
}: GoalFormProps) {
  const [metric, setMetric] = useState<MetricType>(goal?.metric ?? 'softPull');
  const [timeframe, setTimeframe] = useState<TimeframeType>(goal?.timeframe ?? 'weekly');
  const [target, setTarget] = useState<number>(goal?.target ?? 10);
  const [assignmentLevel, setAssignmentLevel] = useState<Goal['assignmentLevel']>(goal?.assignmentLevel ?? 'company');
  const [assignmentId, setAssignmentId] = useState<string>(goal?.assignmentId ?? '');
  // Get appropriate options based on assignment level
  const assignmentOptions = useMemo(() => {
    if (assignmentLevel === 'division') {
      return getAllDivisionsForSelection();
    }
    if (assignmentLevel === 'team') {
      return getAllTeamsForSelection();
    }
    if (assignmentLevel === 'loanOfficer') {
      return getAllLoanOfficersForSelection();
    }
    return [];
  }, [assignmentLevel]);
  const handleAssignmentLevelChange = (newLevel: Goal['assignmentLevel']) => {
    setAssignmentLevel(newLevel);
    // Reset assignment ID when level changes
    setAssignmentId('');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newGoal: Goal = {
      id: goal?.id ?? `goal-${Date.now()}`,
      metric,
      timeframe,
      target,
      assignmentLevel,
      assignmentId: assignmentLevel !== 'company' ? assignmentId : undefined,
      effectiveDate: goal?.effectiveDate ?? new Date().toISOString().split('T')[0]
    };
    onSave(newGoal);
  };
  return <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 transition-colors" data-id="element-163">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6" data-id="element-164">
        {goal ? 'Edit Goal' : 'Add New Goal'}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-165">
        {/* Metric */}
        <div data-id="element-166">
          <label htmlFor="goal-metric" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-167">
            Metric
          </label>
          <select id="goal-metric" value={metric} onChange={e => setMetric(e.target.value as MetricType)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-168">
            <option value="softPull" data-id="element-169">Soft Pull</option>
            <option value="submitted" data-id="element-170">Submitted</option>
          </select>
        </div>

        {/* Timeframe */}
        <div data-id="element-171">
          <label htmlFor="goal-timeframe" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-172">
            Timeframe
          </label>
          <select id="goal-timeframe" value={timeframe} onChange={e => setTimeframe(e.target.value as TimeframeType)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-173">
            <option value="weekly" data-id="element-174">Weekly</option>
            <option value="monthly" data-id="element-175">Monthly</option>
          </select>
        </div>

        {/* Target */}
        <div data-id="element-176">
          <label htmlFor="goal-target" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-177">
            Target
          </label>
          <input id="goal-target" type="number" min={1} value={target} onChange={e => setTarget(parseInt(e.target.value, 10) || 0)} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-178" />
        </div>

        {/* Assignment Level */}
        <div data-id="element-179">
          <label htmlFor="goal-assignment" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-180">
            Assignment Level
          </label>
          <select id="goal-assignment" value={assignmentLevel} onChange={e => handleAssignmentLevelChange(e.target.value as Goal['assignmentLevel'])} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-181">
            <option value="company" data-id="element-182">Company-Wide</option>
            <option value="division" data-id="element-183">Division</option>
            <option value="team" data-id="element-184">Team</option>
            <option value="loanOfficer" data-id="element-185">Loan Officer</option>
          </select>
        </div>

        {/* Assignment Selection (conditional) */}
        {assignmentLevel !== 'company' && <div className="sm:col-span-2" data-id="element-186">
            <label htmlFor="goal-assignment-select" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" data-id="element-187">
              Select{' '}
              {assignmentLevel === 'division' ? 'Division' : assignmentLevel === 'team' ? 'Team' : 'Loan Officer'}
            </label>
            <SearchableSelect options={assignmentOptions} value={assignmentId} onChange={setAssignmentId} placeholder={`Select ${assignmentLevel === 'division' ? 'division' : assignmentLevel === 'team' ? 'team' : 'loan officer'}...`} data-id="element-188" />
          </div>}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100 dark:border-gray-700" data-id="element-189">
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors" data-id="element-190">
          <XIcon className="w-4 h-4" data-id="element-191" />
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm" data-id="element-192">
          <SaveIcon className="w-4 h-4" data-id="element-193" />
          {goal ? 'Update Goal' : 'Save Goal'}
        </button>
      </div>
    </form>;
}